import { TableCell, TableRow, Typography, Button } from '@mui/material'
import OverlayLoading from 'components/atoms/OverlayLoading'
import { ActionText } from 'components/atoms/Text'
import { ColStack } from 'components/atoms/container/InCardStack'
import RowStack from 'components/atoms/container/RowStack'
import ToolTipCustomized from 'components/atoms/container/ToolTipCustomized'
import CheckButton from 'components/atoms/input/CheckButton'
import AvatarHeaderCell from 'components/atoms/table/AvartarHeaderCell'
import PresetTableContainer from 'components/atoms/table/PresetTableContainer'
import NotificationSnackBar from 'components/molecules/notificationOverlay/NotificationSnackBar'
import { useEffect, useState } from 'react'
import { downloadExcel } from 'react-export-table-to-excel'
import { getConstant } from 'services/constantServices'
import {
  CONSTANTS_OPTIONS,
  USER_SURVEYS_URL,
  USER_SURVEY_STATUSES
} from 'services/constants'
import { getRequest, postRequest } from 'utils/api'
import { formatDateTime } from 'utils/formatDateTime'

const ProgramSurveyReportCard = ({ organization }) => {
  const [surveyData, setSurveyData] = useState([])
  const [selected, setSelected] = useState([])
  const [questions, setQuestions] = useState([])
  const [loading, setLoading] = useState(false)
  const [notificationOpen, setNotificationOpen] = useState(null)
  const [requestError, setRequestError] = useState(null)

  const fetchSurvey = async () => {
    const res = await getRequest(USER_SURVEYS_URL, {
      organizationId: organization.id
    })
    if (res.status === 200) {
      const resData = res.data.data
      const users = [...new Map(resData.map(s => [s.user.id, s.user])).values()]
      const userSurveyData = users.map(user => {
        const surveys = resData.filter(s => s.user.id === user.id)
        const userData = { user, preSurvey: surveys[0] }
        if (surveys.length > 1) {
          userData.postSurvey = surveys[surveys.length - 1]
        }

        return userData
      })
      setSurveyData(userSurveyData)
    }
  }
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true)
      if (!questions.length) {
        const quests = await getConstant(
          CONSTANTS_OPTIONS.USER_SURVEY_QUESTIONS
        )
        if (quests) {
          setQuestions(quests)
        }
      }
      await fetchSurvey()
      setLoading(false)
    }
    fetchData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organization])

  const handleSelect = userId => {
    if (selected.includes(userId)) {
      setSelected(selected.filter(s => s !== userId))
    } else {
      setSelected([...selected, userId])
    }
  }
  const isUserSelected = userId => {
    return selected.includes(userId)
  }
  const handleSelectAll = () => {
    const selectable = surveyData.filter(
      s =>
        s.preSurvey?.status !== USER_SURVEY_STATUSES.PENDING &&
        s.postSurvey?.status !== USER_SURVEY_STATUSES.PENDING
    )
    if (selected.length === selectable.length) {
      setSelected([])
    } else {
      setSelected(selectable.map(s => s.user.id))
    }
  }
  const surveyCompleted = survey =>
    survey?.status === USER_SURVEY_STATUSES.COMPLETED

  const handleDownloadExcel = () => {
    const concatSurveys = (s1, survey, sentDate = false) => {
      s1.push(survey?.status)
      if (sentDate) {
        s1.push(survey?.createdAt ? formatDateTime(survey?.createdAt) : '')
      }
      s1.push(surveyCompleted(survey) ? formatDateTime(survey?.updatedAt) : '')
      if (
        surveyCompleted(survey) &&
        survey.scores?.length === questions.length
      ) {
        s1.push(...survey.scores)
      } else {
        s1.push(...Array(questions.length).fill(''))
      }
    }
    const header = [
      'First Name',
      'Last Name',
      'Email',
      'Cohorts',
      'Pre-path Survey',
      'Pre-path Survey Completed Date',
      ...questions.map((v, i) => `Pre-path-${i + 1}`),
      'Post-path Survey',
      'Post-path Survey Sent Date',
      'Post-path Survey Completed Date',
      ...questions.map((v, i) => `Post-path-${i + 1}`)
    ]
    const body = [
      [
        'Question',
        ...Array(5).fill(''),
        ...questions.map(v => v.question),
        ...Array(3).fill(''),
        ...questions.map(v => v.question)
      ]
    ]
    surveyData.forEach(s => {
      const d = [
        s.user.firstName,
        s.user.lastName,
        s.user.email,
        s.cohorts?.map(c => c.name).join(', ')
      ]
      concatSurveys(d, s.preSurvey)
      concatSurveys(d, s.postSurvey, true)
      body.push(d)
    })
    downloadExcel({
      fileName: `Program Survey Report - ${organization.name}`,
      sheet: 'sheet1',
      tablePayload: {
        header,
        body
      }
    })
  }

  const handleSendPostSurvey = async () => {
    setLoading(true)
    const res = await postRequest(USER_SURVEYS_URL, {
      userIds: selected
    })
    if (res.status === 201) {
      setSelected([])
      setRequestError(false)
      await fetchSurvey()
    } else {
      setRequestError(true)
    }
    setNotificationOpen(true)
    setLoading(false)
  }
  return (
    <ColStack width='100%'>
      <RowStack>
        <Button onClick={handleDownloadExcel}>Export Result</Button>
        <Button disabled={selected.length === 0} onClick={handleSendPostSurvey}>
          Send Post Survey
        </Button>
      </RowStack>
      <PresetTableContainer
        headers={
          <>
            <TableCell>
              <RowStack spacing={2}>
                <ActionText
                  variant='h5'
                  sx={{ textDecoration: 'underline' }}
                  onClick={handleSelectAll}>
                  Select All
                </ActionText>
                <ToolTipCustomized toolTip='Only user without pending surveys can be selected' />
              </RowStack>
            </TableCell>
            <TableCell>User Details</TableCell>
            <TableCell>Cohorts</TableCell>
            <TableCell>Pre-path Survey</TableCell>
            <TableCell>Post-path Survey</TableCell>
          </>
        }>
        {surveyData.map(u => (
          <TableRow key={u.user.id}>
            <TableCell>
              <CheckButton
                isChecked={isUserSelected(u.user.id)}
                setIsChecked={() => handleSelect(u.user.id)}
                checkProps={{
                  disabled:
                    u.preSurvey?.status === USER_SURVEY_STATUSES.PENDING ||
                    u.postSurvey?.status === USER_SURVEY_STATUSES.PENDING
                }}
              />
            </TableCell>
            <AvatarHeaderCell user={u.user} />
            <TableCell>
              <Typography>
                {u.user.cohorts?.length
                  ? u.user.cohorts.map(c => c.name).join(', ')
                  : '-'}
              </Typography>
            </TableCell>
            <TableCell>
              <Typography variant='h6'>
                {surveyCompleted(u.preSurvey) ? 'Completed' : '-'}
              </Typography>
              {surveyCompleted(u.preSurvey) && (
                <Typography variant='body1Small' color='textSecondary'>
                  {formatDateTime(u.preSurvey.updatedAt)}
                </Typography>
              )}
            </TableCell>
            <TableCell>
              {u.postSurvey ? (
                <>
                  <Typography variant='h6'>
                    {surveyCompleted(u.postSurvey) ? 'Completed' : 'Pending'}
                  </Typography>
                  <Typography variant='body1Small' color='textSecondary'>
                    {surveyCompleted(u.postSurvey) ? '' : 'Sent at '}
                    {formatDateTime(
                      surveyCompleted(u.postSurvey)
                        ? u.postSurvey.updatedAt
                        : u.postSurvey.createdAt
                    )}
                  </Typography>
                </>
              ) : (
                '-'
              )}
            </TableCell>
          </TableRow>
        ))}
      </PresetTableContainer>
      <OverlayLoading open={loading} />
      <NotificationSnackBar
        open={notificationOpen}
        setOpen={setNotificationOpen}
        success={requestError === false}
        error={requestError}
      />
    </ColStack>
  )
}

export default ProgramSurveyReportCard
